<template>
    <v-app-bar flat class="app-nav brand-rule">
        <v-app-bar-nav-icon v-if="mobile" @click="drawer = !drawer" />
        <router-link :to="{ name: 'home' }" class="app-nav__brand ml-2 mr-4">
            <AppBrand :icon-only="mobile" :height="mobile ? 28 : 30" />
        </router-link>
        <v-tabs v-if="!mobile" class="app-nav__tabs">
            <v-tab v-for="item in items" :key="item.name" :to="{ name: item.name }">{{ item.title }}</v-tab>
        </v-tabs>
        <v-spacer />
        <v-menu>
            <template #activator="{ props: activator }">
                <v-btn v-bind="activator" variant="text" class="app-nav__user text-none" :icon="mobile">
                    <span v-if="!mobile" class="text-body-2 text-medium-emphasis mr-3">{{ session.user?.userName }}</span>
                    <v-avatar :color="session.impersonating ? 'warning' : 'accent'" size="30">{{ initial }}</v-avatar>
                </v-btn>
            </template>
            <v-list density="comfortable" min-width="240">
                <v-list-item :title="session.user?.userName ?? ''" :subtitle="signedInAs" />
                <v-divider />
                <v-list-item :to="{ name: 'profile' }" title="Profile" prepend-icon="mdi-account-outline" />
                <v-list-item v-if="session.impersonating" title="Stop impersonating" prepend-icon="mdi-account-switch" @click="stop" />
                <v-list-item title="Log out" prepend-icon="mdi-logout" @click="logout" />
            </v-list>
        </v-menu>
    </v-app-bar>
    <v-navigation-drawer v-if="mobile" v-model="drawer" temporary>
        <v-list nav>
            <v-list-item v-for="item in items" :key="item.name" :to="{ name: item.name }" :title="item.title" :prepend-icon="item.icon" />
        </v-list>
        <template #append>
            <v-list nav>
                <v-list-item :to="{ name: 'profile' }" title="Profile" prepend-icon="mdi-account-outline" />
                <v-list-item v-if="session.impersonating" title="Stop impersonating" prepend-icon="mdi-account-switch" @click="stop" />
                <v-list-item title="Log out" prepend-icon="mdi-logout" @click="logout" />
            </v-list>
            <div class="pa-3 text-caption text-medium-emphasis">{{ session.user?.userName }}<br v-if="session.impersonating" />{{ signedInAs }}</div>
        </template>
    </v-navigation-drawer>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { useDisplay } from "vuetify";
import AppBrand from "./AppBrand.vue";
import { useSessionStore } from "@/store/session";
import { stopImpersonating } from "@/utils/impersonation";

const { mobile } = useDisplay();
const session = useSessionStore();
const items = computed(() => [
    { name: "orders", title: "Order Tracking", icon: "mdi-package-variant-closed" },
    { name: "inventory", title: "Inventory", icon: "mdi-warehouse" },
    { name: "manufacturers", title: "Manufacturers", icon: "mdi-factory" },
    ...(session.user?.isAdmin ? [{ name: "admin-users", title: "Admin", icon: "mdi-shield-account" }] : []),
]);
const router = useRouter();
const drawer = ref(false);
const initial = computed(() => session.user?.userName.charAt(0).toUpperCase() ?? "");
const signedInAs = computed(() => (session.impersonating ? `Impersonated by ${session.impersonating.userName}` : "Signed in"));

async function logout(): Promise<void> {
    await session.logout();
    await router.push({ name: "login" });
}

async function stop(): Promise<void> {
    await stopImpersonating(router);
}
</script>

<style lang="scss">
.app-nav {
    &__brand {
        display: flex;
        align-items: center;
    }

    &__tabs {
        align-self: stretch;
    }
}
</style>
